import type React from 'react'
import { useEffect, useState } from 'react'
import { Search, Plus, X, ChefHat, Sparkles, Lock } from 'lucide-react'
import { recipesApi, type Recipe } from '../../services/api'
import { useAuth } from '../../contexts/useAuth'
import toast from 'react-hot-toast'

interface Props {
    state: {
        name: string
        selectedRecipes: Recipe[]
        nbPersons: number
        durationDays: number
        mealsPerDay: number
    }
    update: (patch: Partial<Props['state']>) => void
    onNext: () => void
}



export default function Step1Meals({ state, update, onNext }: Props) {
    const [query, setQuery] = useState('')
    const [results, setResults] = useState<Recipe[]>([])
    const [allRecipes, setAllRecipes] = useState<Recipe[]>([])
    const [searching, setSearching] = useState(false)
    const [loadingAll, setLoadingAll] = useState(false)
    const { isAuthenticated, loading: authLoading } = useAuth()

    useEffect(() => {
        if (authLoading) return

        let active = true
        const loadAllRecipes = async () => {
            try {
                setLoadingAll(true)
                const data = await recipesApi.list('', 'plat', false)
                if (!active) return
                // Sort by name alphabetically
                const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name, 'fr'))
                setAllRecipes(sorted)
            } catch {
                // keep silent
            } finally {
                if (active) setLoadingAll(false)
            }
        }
        loadAllRecipes()

        return () => {
            active = false
        }
    }, [isAuthenticated, authLoading])

    useEffect(() => {
        if (!query.trim()) { setTimeout(() => setResults([]), 0); return }
        const t = setTimeout(() => {
            setSearching(true)
            recipesApi.list(query, 'plat').then((res: Recipe[]) => {
                const sorted = [...res].sort((a, b) => a.name.localeCompare(b.name, 'fr'))
                setResults(sorted)
            }).catch(() => { }).finally(() => setSearching(false))
        }, 300)
        return () => clearTimeout(t)
    }, [query])

    const toggleRecipe = (r: Recipe) => {
        const already = state.selectedRecipes.find(x => x.id === r.id)
        if (already) {
            update({ selectedRecipes: state.selectedRecipes.filter(x => x.id !== r.id) })
        } else {
            update({ selectedRecipes: [...state.selectedRecipes, r] })
            setQuery('')
            setResults([])
        }
    }

    const handleLLMSearch = async () => {
        if (!query.trim()) return
        try {
            toast.loading('Recherche avec IA…', { id: 'llm' })
            const res = await recipesApi.llmSearch(query)
            toast.success('Recette trouvée via IA !', { id: 'llm' })
            const stub: Recipe = {
                id: `llm-${Date.now()}`,
                name: query,
                servings: 4,
                is_personal: false,
                source: 'llm',
                recipe_ingredients: res.ingredients?.map((i: { name: string; quantity_per_portion: number; unit: string }, idx: number) => ({
                    id: `llm-ri-${idx}`,
                    name: i.name,
                    quantity: i.quantity_per_portion,
                    unit: i.unit,
                })) ?? [],
            }
            update({ selectedRecipes: [...state.selectedRecipes, stub] })
            setQuery('')
            setResults([])
        } catch {
            toast.error('Erreur IA', { id: 'llm' })
        }
    }

    const displayRecipes = (query.trim() ? results : allRecipes).slice(0, 10)

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Search Header */}
            <div className="card" style={{ padding: '20px', background: 'var(--bg-2)', border: '1px solid var(--border)' }}>
                <div className="form-group">
                    <label className="form-label" style={{ marginBottom: 4 }}>Que voulez-vous cuisiner ?</label>
                    <div className="input-group">
                        <div className="input-icon-wrap" style={{ flex: 1 }}>
                            <Search size={18} className="input-icon" />
                            <input
                                className="input"
                                value={query}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                                placeholder="Rechercher un plat (ex: Lasagnes, Poulet cury...)"
                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && results.length === 0 && isAuthenticated && handleLLMSearch()}
                            />
                        </div>
                        {query && results.length === 0 && !searching && isAuthenticated && (
                            <button className="btn btn-secondary btn-sm" onClick={handleLLMSearch} title="Chercher avec IA">
                                <Sparkles size={16} />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Selected recipes horizontal chips */}
            {state.selectedRecipes.length > 0 && (
                <div>
                    <p className="form-label" style={{ marginBottom: 8 }}>Sélectionnés ({state.selectedRecipes.length})</p>
                    <div className="chips">
                        {state.selectedRecipes.map(r => (
                            <div key={r.id} className="chip selected" style={{ animation: 'fadeIn 0.2s ease-out' }}>
                                {r.source === 'llm' && <Sparkles size={12} />}
                                {r.name}
                                <button 
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, display: 'flex', marginLeft: 4 }} 
                                    onClick={() => toggleRecipe(r)}
                                >
                                    <X size={13} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Recipes List */}
            <div style={{ flex: 1 }}>
                <p className="section-title" style={{ marginTop: 0 }}>
                    {query.trim() ? 'Résultats de recherche' : 'Tous les plats (A-Z)'}
                </p>
                
                {loadingAll && !query.trim() && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {[1, 2, 3].map(i => <div key={i} className="skeleton" style={{ height: 48 }} />)}
                    </div>
                )}

                {!loadingAll && displayRecipes.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {displayRecipes.map((r: Recipe, idx: number) => {
                            const selected = !!state.selectedRecipes.find(x => x.id === r.id)
                            return (
                                <button
                                    key={r.id}
                                    className={`recipe-search-result ${selected ? 'selected' : ''}`}
                                    onClick={() => toggleRecipe(r)}
                                    style={{ 
                                        animation: `slideUp 0.3s ease ${Math.min(idx * 0.03, 0.5)}s both`,
                                        padding: '12px 16px',
                                        borderRadius: 'var(--radius-sm)'
                                    }}
                                >
                                    <ChefHat size={18} color={selected ? 'var(--primary-light)' : 'var(--text-3)'} />
                                    <span style={{ flex: 1, textAlign: 'left', fontWeight: selected ? 600 : 400 }}>{r.name}</span>
                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginRight: 4 }}>{r.servings} p.</span>
                                    {selected ? <X size={16} color="var(--primary-light)" /> : <Plus size={16} />}
                                </button>
                            )
                        })}
                    </div>
                )}

                {searching && <p style={{ fontSize: '0.9rem', color: 'var(--text-3)', textAlign: 'center', padding: '20px' }}>Recherche en cours…</p>}
                
                {query && !searching && results.length === 0 && (
                    <div className="empty-state" style={{ padding: '20px' }}>
                        <p style={{ fontSize: '0.9rem', color: 'var(--text-3)' }}>
                            Aucun plat trouvé pour "{query}"
                        </p>
                        {isAuthenticated ? (
                            <button className="btn btn-secondary btn-sm" onClick={handleLLMSearch} style={{ marginTop: 10 }}>
                                <Sparkles size={14} /> Demander à l'IA de le créer
                            </button>
                        ) : (
                            <p style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginTop: 8 }}>
                                <Lock size={12} /> Connectez-vous pour utiliser la recherche IA
                            </p>
                        )}
                    </div>
                )}
            </div>

            <div style={{ position: 'sticky', bottom: 0, background: 'var(--bg)', padding: '16px 0', borderTop: '1px solid var(--border)', display: 'flex', gap: 10 }}>
                <button
                    className="btn btn-primary btn-full"
                    disabled={state.selectedRecipes.length === 0}
                    onClick={onNext}
                    style={{ padding: '16px' }}
                >
                    Continuer ({state.selectedRecipes.length} sélectionné{state.selectedRecipes.length > 1 ? 's' : ''}) →
                </button>
            </div>
        </div>
    )
}
